import { useMemo } from "react";
import {
  colombia,
  getDepartment,
  getMunicipalityName,
} from "@pahcertificar/certmanager-locations";
import { Field } from "./Input";
import { Combobox, type ComboboxOption } from "./Combobox";

export interface LocationPickerProps {
  departmentCode: string | null;
  municipalityCode: string | null;
  onDepartmentChange: (code: string | null) => void;
  onMunicipalityChange: (code: string | null) => void;
  required?: boolean;
  className?: string;
  layout?: "stacked" | "grid";
}

export function LocationPicker({
  departmentCode,
  municipalityCode,
  onDepartmentChange,
  onMunicipalityChange,
  required,
  className,
  layout = "grid",
}: LocationPickerProps) {
  const departmentOptions = useMemo<ComboboxOption[]>(
    () =>
      colombia.departments.map((d) => ({
        value: d.code,
        label: d.name,
        sublabel: d.code,
      })),
    []
  );

  const municipalityOptions = useMemo<ComboboxOption[]>(() => {
    if (!departmentCode) return [];
    const dept = getDepartment(departmentCode);
    if (!dept) return [];
    return dept.municipalities.map((m) => ({
      value: m.code,
      label: m.name,
      sublabel: m.code,
    }));
  }, [departmentCode]);

  const containerClass =
    layout === "grid"
      ? "grid grid-cols-1 md:grid-cols-2 gap-4"
      : "flex flex-col gap-4";

  return (
    <div className={[containerClass, className].filter(Boolean).join(" ")}>
      <Field label="Departamento" required={required} hint="DANE code">
        <Combobox
          value={departmentCode}
          onChange={(code) => {
            onDepartmentChange(code);
            // Reset municipality when department changes
            if (municipalityCode) onMunicipalityChange(null);
          }}
          options={departmentOptions}
          placeholder="Select departamento"
          emptyLabel="No departments match"
        />
      </Field>
      <Field
        label="Municipio"
        required={required}
        hint={
          departmentCode
            ? getMunicipalityName(municipalityCode ?? "") ?? "DANE code"
            : "Pick a departamento first"
        }
      >
        <Combobox
          value={municipalityCode}
          onChange={onMunicipalityChange}
          options={municipalityOptions}
          placeholder={departmentCode ? "Select municipio" : "Pick a departamento first"}
          disabled={!departmentCode}
          disabledReason="Select a departamento first"
          emptyLabel="No municipios match"
        />
      </Field>
    </div>
  );
}
